import { useEffect, useState } from 'preact/hooks';
import { api, type ExpandedEvent, type Me, type UserBasic, type Child } from '../lib/api.js';
import { ChevronLeft, ChevronRight, MapPin, Repeat } from 'lucide-preact';

const ROLE_LABELS: Record<string, string> = {
  bring: 'bringt',
  holt_ab: 'holt ab',
  bleibt_dabei: 'dabei',
  kümmert_sich_komplett: 'betreut',
  kinderbetreuer: 'betreut',
  child_owner: '',
};

function weekBounds(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const diffToMon = (day + 6) % 7;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - diffToMon + offsetWeeks * 7);
  const nextMonday = new Date(monday);
  nextMonday.setDate(nextMonday.getDate() + 7);
  return { from: monday, to: nextMonday };
}

function fmt(d: string): string {
  const dt = new Date(d);
  return dt.toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fmtRange(a: string, b: string): string {
  const sa = new Date(a).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const sb = new Date(b).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${sa}–${sb}`;
}

type Props = { me: Me };

export function Week({ me: _me }: Props) {
  const [offset, setOffset] = useState(0);
  const [events, setEvents] = useState<ExpandedEvent[] | null>(null);
  const [users, setUsers] = useState<UserBasic[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<UserBasic[]>('/api/v1/users').then(setUsers).catch(() => {});
    api<Child[]>('/api/v1/children').then(setChildren).catch(() => {});
  }, []);

  useEffect(() => {
    const { from, to } = weekBounds(offset);
    setEvents(null);
    setError(null);
    api<ExpandedEvent[]>(`/api/v1/events?from=${from.toISOString()}&to=${to.toISOString()}`)
      .then((rows) => {
        rows.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
        setEvents(rows);
      })
      .catch((e) => setError(String(e)));
  }, [offset]);

  function participantLabel(p: ExpandedEvent['participants'][number]): { name: string; role: string; color: string | null } {
    const role = ROLE_LABELS[p.role] ?? p.role;
    if (p.actorType === 'external') {
      return { name: p.actorExternalName ?? '—', role, color: null };
    }
    if (p.actorType === 'user') {
      const u = users.find((uu) => uu.id === p.actorUserId);
      return { name: u?.displayName.split(' ')[0] ?? '?', role, color: u?.color ?? null };
    }
    if (p.actorType === 'child') {
      const c = children.find((cc) => cc.id === p.actorChildId);
      return { name: c?.name ?? '?', role, color: c?.color ?? null };
    }
    return { name: '—', role, color: null };
  }

  const { from, to } = weekBounds(offset);

  return (
    <div class="screen">
      <div class="row" style={{ alignItems: 'center' }}>
        <button class="ghost" style={{ padding: '6px 10px' }} onClick={() => setOffset((x) => x - 1)} aria-label="Vorige Woche">
          <ChevronLeft size={20} />
        </button>
        <h1 style={{ textAlign: 'center', margin: 0 }}>
          {from.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – {new Date(to.getTime() - 86400_000).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
        </h1>
        <button class="ghost" style={{ padding: '6px 10px' }} onClick={() => setOffset((x) => x + 1)} aria-label="Nächste Woche">
          <ChevronRight size={20} />
        </button>
      </div>

      {error ? <div class="error">{error}</div> : null}
      {events === null ? <p class="muted" style={{ textAlign: 'center' }}><span class="spinner" /></p> : null}
      {events && events.length === 0 ? <p class="muted" style={{ textAlign: 'center', marginTop: 24 }}>Keine Events in dieser Woche.</p> : null}

      <div class="stack" style={{ marginTop: 14 }}>
        {events?.map((e) => (
          <a key={`${e.parentEventId}-${e.startsAt}`} href={`/event/${e.parentEventId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <div class="card event-card">
              <div class="bar" />
              <div class="body">
                <div class="title">
                  {e.title}
                  {e.recurrenceRule ? <Repeat size={13} style={{ marginLeft: 6, opacity: 0.6 }} /> : null}
                </div>
                <div class="meta">
                  {fmt(e.startsAt)} · {fmtRange(e.startsAt, e.endsAt)}
                  {e.location ? <> · <MapPin size={12} style={{ display: 'inline', verticalAlign: '-1px' }} /> {e.location}</> : null}
                </div>
                {e.participants.length > 0 ? (
                  <div style={{ marginTop: 6 }}>
                    {e.participants.map((p) => {
                      const { name, role, color } = participantLabel(p);
                      return (
                        <span class={`tag ${p.acceptStatus}`} title={p.role}>
                          {color ? <span class="dot" style={{ background: color }} /> : null}
                          <span class="tname">{name}</span>
                          {role ? <span class="trole">{role}</span> : null}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
